import { Button } from "antd";
import { API_URL } from "env";
import Layout from "Layout";
import { ArrowDown, ArrowDownToLine, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./style.scss";

interface ReferredUser {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    created_at: string;
}

const Affiliate: React.FC = () => {
    const { t } = useTranslation();
    const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
    const [referralCode, setReferralCode] = useState<string>('');
    const [referralLink, setReferralLink] = useState<string>('');
    const [referralUser, setReferralUser] = useState<ReferredUser[]>([]);
    const [progressWidth, setProgressWidth] = useState<string>("0%");
    const [copiedCode, setCopiedCode] = useState<boolean>(false);
    const [copiedLink, setCopiedLink] = useState<boolean>(false);

    const milestones = [0, 5, 25, 50, 100];
    const totalWidth = 75;

    const calculateProgressWidth = (progress: number) => {
        if (progress >= milestones[milestones.length - 1]) {
            return `${totalWidth}%`;
        }

        const milestoneIndices = milestones.map((m, i) => ({ value: m, index: i }));
        const currentMilestone =
            milestoneIndices.find((m) => m.value >= progress) ||
            milestoneIndices[milestones.length - 1];
        const prevMilestone =
            milestoneIndices[currentMilestone.index - 1] || { value: 0, index: 0 };

        const segmentCount = milestones.length - 1;
        const baseWidthPerSegment = totalWidth / segmentCount;
        const progressInSegment =
            (progress - prevMilestone.value) /
            (currentMilestone.value - prevMilestone.value);
        const widthOffset = (prevMilestone.index / segmentCount) * totalWidth;
        const segmentProgressWidth = progressInSegment * baseWidthPerSegment;

        return `${widthOffset + segmentProgressWidth}%`;
    };

    const fetchReferralInfo = async () => {
        try {
            const response = await fetch(`${API_URL}/api/v1/be/affiliate/stats`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })

            const data = await response.json()
            if (data.status) {
                setReferralCode(data.data.referral_code)
                setReferralLink(`${window.location.protocol}//${window.location.host}/sign-up?referral_code=${data.data.referral_code}`)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchReferralUser = async () => {
        try {
            const response = await fetch(`${API_URL}/api/v1/be/affiliate/referred-users`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })

            const data = await response.json()
            if (data.status && Array.isArray(data.data.referred_users)) {
                console.log(data.data.referred_users)
                setReferralUser(data.data.referred_users);
            } else {
                console.error("Invalid or missing referred_users in response:", data);
                setReferralUser([]);
            }
        } catch (error) {
            console.error(error)
        }
    }

    const handleCopy = async (text: string, type: 'code' | 'link') => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${type === 'code' ? 'Referral code' : 'Referral link'} copied to clipboard!`);
            if (type === 'code') {
                setCopiedCode(true);
                setTimeout(() => setCopiedCode(false), 2000);
            } else {
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
            }
        } catch (error) {
            console.error("Failed to copy:", error);
            toast.error("Failed to copy to clipboard.");
        }
    };

    useEffect(() => {
        const progress = Math.max(0, referralUser.length);
        const width = calculateProgressWidth(progress)

        setProgressWidth(width)
    }, [referralUser])

    useEffect(() => {
        fetchReferralUser()
        fetchReferralInfo()
    }, [])

    return (
        <Layout>
            <div className="section-header mb-4">
                <div className="section-title">
                    <div className="section-title__title-wrap">
                        <div className="section-title__icon">
                            <i className="fi fi--user fi--themed"></i>
                        </div>
                        <div className="section-title--main">{t("Profile")}</div>
                    </div>
                </div>
                <div className="section-header__tabs">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <a href="/profile" className="nav-link ">
                                <span className="nav-link__label">{t('PersonalInformation')}</span>
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="/profile/account" className="nav-link">
                                <span className="nav-link__label">{t('AccountInformation')}</span>
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="/profile/security" className="nav-link ">
                                <span className="nav-link__label">{t('Security')}</span>
                            </a>
                        </li>
                        {/* <li className="nav-item">
                            <a href="/profile/teams" className="nav-link ">
                                <span className="nav-link__label">{t("Teams")}</span>
                            </a>
                        </li> */}
                        <li className="nav-item">
                            <a href="/profile/affiliate" className="nav-link active">
                                <span className="nav-link__label">{t('Affiliates')}</span>
                            </a>
                        </li>
                        <li className="nav-item">
                            <a href="/profile/inventory" className="nav-link ">
                                <span className="nav-link__label">{t('Inventory')}</span>
                            </a>
                        </li>
                        <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                            <a href="#" data-notification-target="nav-profile-identity" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                <span className="nav-link__label">{t('LUMIRIdentity')}</span>
                            </a>
                        </li>
                        <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                            <a href="#" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                <span className="nav-link__label">{t('EmailNewsletters')}</span>
                            </a>
                        </li>
                        <li className="nav-item" style={{ pointerEvents: 'none', cursor: 'default', color: 'gray', opacity: '0.6' }}>
                            <a href="#" className="nav-link disabled" tabIndex={-1} aria-disabled="true">
                                <span className="nav-link__label">{t('LUMIRPoints')}</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="text-white min-h-screen p-4 max-md:p-2">
                <div className="flex max-md:flex-col justify-between items-center mb-4 max-md:mb-2">
                    <h1 className="text-2xl max-md:text-xl font-bold mb-4 max-md:mb-2 whitespace-nowrap">{t('affiliateTilte')}</h1>
                    <div className="flex gap-1">
                        <button type="button" className="btn-primary btn">
                            {t('MarketingAssets')}
                        </button>
                        <button type="button" className="btn-primary btn">
                            {t('AffiliatePolicies')}
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 max-md:grid-cols-1 gap-4 max-md:gap-2">
                    <div className="portlet">
                        <h2 className="text-lg max-md:text-base font-semibold mb-2 max-md:mb-1">{t('ReferralCode')}</h2>
                        <p className="text-gray-500 mb-2 max-md:mb-1 text-sm max-md:text-xs">
                            {t('referralNote')}
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={referralCode ? referralCode : ''}
                                readOnly
                                className="input-code bg-[#f4f4f4] text-black py-2 max-md:py-1 px-3 max-md:px-2 rounded-l-lg w-full max-md:w-auto flex-1"
                            />
                            <button
                                className="btn-primary btn"
                                onClick={() => handleCopy(referralCode, 'code')}
                            >
                                {copiedCode ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                    <div className="portlet">
                        <h2 className="text-lg max-md:text-base font-semibold mb-2 max-md:mb-1">{t('ShareTheReferralLink')}</h2>
                        <p className="text-gray-400 mb-2 max-md:mb-1 text-sm max-md:text-xs">
                            {t('ShareReferralNote')}
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={referralLink ? referralLink : ''}
                                readOnly
                                className="input-code bg-[#f4f4f4] text-black py-2 max-md:py-1 px-3 max-md:px-2 rounded-l-lg w-full max-md:w-auto flex-1"
                            />
                            <button
                                className="btn-primary btn"
                                onClick={() => handleCopy(referralLink, 'link')}
                            >
                                {copiedLink ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="portlet mt-6 max-md:mt-4">
                    <h2 className="text-xl font-semibold mb-2 max-md:text-lg max-md:mb-1">
                        {t('affiliateSectionTilte1')}
                    </h2>
                    <p className="text-gray-400 mb-2 max-md:text-sm max-md:mb-1">
                        {t('affiliateSectionDesc1')}
                    </p>
                    <h2 className="flex !text-sm font-semibold ml-1 mb-2 max-md:ml-1 max-md:text-xs z-20">
                        {t('Invited')} ({referralUser.length})
                    </h2>
                    <div className="w-full h-6 max-md:h-4 relative">
                        <div className="absolute top-0 left-0 w-[80%] h-6 max-md:h-4 flex justify-between items-center z-10 px-1 max-md:px-0.5">
                            <span className="w-8 max-md:w-5 h-8 max-md:h-5 bg-[#efefef] rounded-full"></span>
                            <span className="w-8 max-md:w-5 h-8 max-md:h-5 bg-[#efefef] rounded-full"></span>
                            <span className="w-8 max-md:w-5 h-8 max-md:h-5 bg-[#efefef] rounded-full"></span>
                            <span className="w-8 max-md:w-5 h-8 max-md:h-5 bg-[#efefef] rounded-full"></span>
                            <span className="w-8 max-md:w-5 h-8 max-md:h-5 bg-[#efefef] rounded-full"></span>
                        </div>
                        <div className="absolute top-0 left-0 w-[80%] h-6 max-md:h-4 flex justify-between items-center z-20 px-1 max-md:px-0.5">
                            {milestones.map((milestone, index) => (
                                <div key={index} className="flex items-center justify-center w-8 max-md:w-5 h-8 max-md:h-5">
                                    <span
                                        className={`w-6 max-md:w-3 h-6 max-md:h-3 rounded-full ${referralUser.length >= milestone ? "bg-[#E9B460]" : "bg-transparent"
                                            }`}
                                    ></span>
                                </div>
                            ))}
                        </div>
                        <div className="absolute top-1.5 max-md:top-1 left-4 w-[calc(100%-8px)] h-3.5 max-md:h-2 bg-[#efefef] z-10 rounded-r-lg"></div>
                        <div
                            className="absolute top-2.5 max-md:top-1.5 left-4 h-1.5 max-md:h-1 bg-[#E9B460] z-15 rounded-r-lg"
                            style={{
                                width:
                                    window.innerWidth <= 767
                                        ? `calc(${progressWidth} - 5%)`
                                        : window.innerWidth <= 1024
                                            ? `calc(${progressWidth} - 2%)`
                                            : progressWidth,
                            }}
                        ></div>
                    </div>
                    <div className="flex w-[80%] justify-between text-gray-400 text-sm max-md:text-xs mt-2 max-md:mt-1 px-1 max-md:px-0.5">
                        <span>0</span>
                        <span>5</span>
                        <span>25</span>
                        <span>50</span>
                        <span>100</span>
                    </div>
                </div>
                <div className="portlet mt-6">
                    <div className="flex justify-between">
                        <div className="relative max-md:w-[50%] w-[80%]">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-400">
                                <Search className="h-5 w-5" />
                            </span>
                            <input
                                type="text"
                                placeholder="Search..."
                                className="text-black placeholder-gray-500 w-full pl-8 pr-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                            />
                        </div>
                        <button
                            type="button"
                            className="btn-secondary btn !flex items-center gap-1 px-4 py-2"
                        >
                            <span>Export</span>
                            <ArrowDownToLine className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Table Section */}
                    <table className="ftmo-table w-100 table--stretched metrix__table-account mt-4">
                        <thead>
                            <tr className="text-black">
                                <th className="p-2 cursor-pointer">ID ↓</th>
                                <th className="p-2 cursor-pointer">Email ↓</th>
                                <th className="p-2 cursor-pointer">{t('FirstName')} ↓</th>
                                <th className="p-2 cursor-pointer">{t('LastName')} ↓</th>
                                <th className="p-2">{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referralUser.map((item, index) => (
                                <tr key={index} className="border-b border-gray-700 text-black">
                                    <td className="p-2">{item.id}</td>
                                    <td className="p-2">{item.email}</td>
                                    <td className="p-2">{item.first_name}</td>
                                    <td className="p-2">{item.last_name}</td>
                                    <td className="p-2 relative">
                                        <button
                                            className="btn-primary btn px-2 py-1 text-white"
                                            onClick={() =>
                                                setOpenDropdownIndex(
                                                    openDropdownIndex === index ? null : index
                                                )
                                            }
                                        >
                                            {t("Contact")}
                                        </button>
                                        {openDropdownIndex === index && (
                                            <div className="absolute text-center left-1 mt-2 w-20 bg-gray-700 text-white rounded shadow-lg z-10">
                                                <div
                                                    className="p-2 hover:bg-gray-600 cursor-pointer"
                                                    onClick={() => {
                                                        setOpenDropdownIndex(null);
                                                        // Handle Email action
                                                    }}
                                                >
                                                    Email
                                                </div>
                                                <div
                                                    className="p-2 hover:bg-gray-600 cursor-pointer"
                                                    onClick={() => {
                                                        setOpenDropdownIndex(null);
                                                        // Handle Phone action
                                                    }}
                                                >
                                                    {t('Phone')}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default Affiliate;